import { Button } from "@/components/ui/Button";
import type { ParsedExtraction } from "../types";

interface DownloadStepProps {
  data: ParsedExtraction;
  pdfUrl: string;
  filename: string;
  onStartOver: () => void;
  onExportExcel?: () => void;
  onBackToReview?: () => void;
}

export function DownloadStep({
  data,
  pdfUrl,
  filename,
  onStartOver,
  onExportExcel,
  onBackToReview,
}: DownloadStepProps) {
  const activeCount = data.objections.filter((o) => !o.resolved).length;
  // The Excel export splits objections into per-assignee tabs, so every active
  // objection needs an assignee. The PDF doesn't, so a user can land here with
  // some still unassigned — in that case, gate Excel and send them back.
  const unassignedCount = data.objections.filter(
    (o) => !o.resolved && !o.assignedTo,
  ).length;
  const generatedAt = new Date();

  return (
    <div className="animate-reveal max-w-3xl">
      <div className="mb-8 flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sage-soft text-sage-ink">
          <CheckGlyph />
        </span>
        <div>
          <div className="label">Complete</div>
          <h1 className="text-[clamp(2rem,4vw,2.75rem)] font-medium tracking-tight text-ink">
            AI1 form ready
          </h1>
        </div>
      </div>

      <p className="text-[1.05rem] leading-relaxed text-ink-soft">
        Your AI1 has been generated successfully.
      </p>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <a
          href={pdfUrl}
          download={filename}
          className="inline-flex h-12 items-center gap-2 rounded-md bg-primary px-6 text-[1rem] font-medium text-white shadow-soft hover:bg-primary-ink hover:shadow-lift transition-all"
        >
          <DownGlyph />
          Download AI1 PDF
        </a>
        {onExportExcel && (
          <Button
            intent="secondary"
            size="lg"
            onClick={onExportExcel}
            disabled={unassignedCount > 0}
            title={
              unassignedCount > 0
                ? `Assign ${unassignedCount} objection${unassignedCount === 1 ? "" : "s"} in Review to export`
                : undefined
            }
          >
            <SpreadsheetGlyph />
            Export to Excel
          </Button>
        )}
        <Button intent="ghost" size="lg" onClick={onStartOver}>
          <RefreshGlyph />
          Create another
        </Button>
      </div>

      {unassignedCount > 0 && (
        <p className="mt-4 text-[0.9rem] text-ink-soft">
          {unassignedCount} objection{unassignedCount === 1 ? "" : "s"}{" "}
          {unassignedCount === 1 ? "has" : "have"} no assignee. The PDF above is
          complete — assign them in Review to enable the Excel export.
        </p>
      )}

      {onBackToReview && (
        <div className="mt-4">
          <button
            type="button"
            onClick={onBackToReview}
            className="text-[0.92rem] text-ink-muted hover:text-ink"
          >
            ← Back to Review to edit objections
          </button>
        </div>
      )}

      <section className="mt-10 rounded-lg border border-rule bg-surface shadow-soft">
        <header className="border-b border-rule px-6 py-3">
          <div className="text-[0.95rem] font-semibold text-ink">
            Form details
          </div>
        </header>
        <dl className="grid grid-cols-12 gap-x-6 gap-y-4 px-6 py-5 text-[0.95rem]">
          <Detail k="Property" v={data.address ?? "—"} />
          <Detail k="Job Number" v={data.jobNumber ?? "—"} mono />
          <Detail k="Objections" v={String(activeCount)} mono />
          <Detail
            k="Generated"
            v={generatedAt.toLocaleString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })}
          />
          <Detail k="Filename" v={filename} mono />
        </dl>
      </section>
    </div>
  );
}

function Detail({
  k,
  v,
  mono,
}: {
  k: string;
  v: string;
  mono?: boolean;
}) {
  return (
    <>
      <dt className="col-span-3 text-[0.82rem] font-medium text-ink-muted">
        {k}
      </dt>
      <dd
        className={
          "col-span-9 " + (mono ? "font-mono text-[0.88rem] text-ink" : "text-ink")
        }
      >
        {v}
      </dd>
    </>
  );
}

function CheckGlyph() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
      <path
        d="M6 14.5l5 5L22 8.5"
        stroke="currentColor"
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DownGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M8 2v9m0 0l-3.5-3.5M8 11l3.5-3.5M3 14h10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RefreshGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M2 8a6 6 0 0 1 10.2-4.2L14 5.5M14 5.5V2M14 5.5h-3.5M14 8a6 6 0 0 1-10.2 4.2L2 10.5M2 10.5V14M2 10.5h3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpreadsheetGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2.5 6h11M2.5 10h11M6 2.5v11M10 2.5v11" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}
